import React from 'react';
import axios from 'axios';
// TODO: APP-3570 Remove when rets2 decodes
import { decode } from 'html-entities';
import { isAuthForRole } from 'utils';
import { Icon } from 'components/Icon';
import { Thumbnail } from 'components/Thumbnail';
import Contact from 'components/Contact';
import Tooltip from 'components/Tooltip';
import Tag from 'components/Tag';

import { OrgFeatures } from '@bb/domain/dist/types';
import {
  SearchType,
  SearchOptions,
  ColumnOptions,
  FormattedListingType,
  ListingResponseType,
} from './types';

import { addListingToHistory } from '../../api/RecentSearchHistoryController';
import ListingRowActions from './ListingRowActions';
import { BrokerageInfo, ListingInfo } from './components';
import { availabilityOrder } from './constants';

export const mapListingResultsToTable = (
  listing: any,
  forceBlur: () => void,
  length: number,
  {
    auth,
    favorites,
    toggleFavorite,
    searchFromTour,
    openTourListingModal,
  }: SearchOptions,
) => {
  const {
    id,
    thumbnail,
    fullAddress,
    line1,
    line2,
    city,
    province,
    reps,
    list_price: price,
    status,
    mls_numbers,
    availability,
    location,
    org,
    dom,
    acceptsRegisteredOffers,
    noMoreShowings,
    team,
    autoConfirm,
    preBookingInformation,
  } = listing;
  const availabilityLabel = availability.replace(/_/g, ' ');
  const chatAgents = reps
    .filter(
      (rep: any) =>
        rep.orgHasChatFeature &&
        rep.id !== auth.id &&
        (!rep.isAgentChatHidden ||
          isAuthForRole({
            auth,
            org: rep.org,
            location: rep.location,
          })),
    )
    .map((rep: any) => rep.id);
  return {
    id,
    thumbnail,
    key: id,
    availability: availabilityLabel as any,
    location,
    fullAddress: decode(fullAddress),
    line1,
    line2,
    city,
    province,
    reps: reps.map((rep: any) => ({
      ...rep,
      firstName: rep.firstName,
      lastName: rep.lastName,
      fullName: rep.fullName,
    })),
    org,
    dom,
    noMoreShowings,
    autoConfirm,
    preBookingInformation,
    team,
    price,
    status: {
      type: status,
      mlsNumbers: mls_numbers,
    },
    orgId: org.id,
    acceptsRegisteredOffers,
    auth,
    favorites,
    toggleFavorite,
    forceBlur,
    searchFromTour,
    openTourListingModal,
    chatAgents,
    listing: { ...listing, org: org.id },
    value:
      (availabilityOrder.indexOf(availabilityLabel) + length + 1) %
      (length + 1),
    showScheduleBtn:
      org.features.includes('APPOINTMENT') &&
      (listing.canEdit || !noMoreShowings),
    listingOrgHasFeature: (feature: OrgFeatures) => {
      const isAssumed = Boolean(auth?.assumingUserId || auth?.isAssumedNotBB);
      if (isAssumed && auth?.org.id === listing.org.id) return true;

      return listing.org.features.includes(feature);
    },
  };
};

const getListingSearchCrossOrg: (
  options: ColumnOptions,
  setActiveRow: React.Dispatch<React.SetStateAction<null | number>>,
) => SearchType<FormattedListingType> = (options, setActiveRow) => ({
  columns: [
    {
      title: '',
      dataIndex: 'listing',
      key: 'preview',
      className: 'eye-column cross-org',
      width: 85,
      render: (
        listing: FormattedListingType['listing'],
        { auth, location, forceBlur },
      ) => (
        <>
          <Thumbnail
            listing={
              (auth.roles[auth.role] === auth.roles.BRANCH_ADMIN &&
                [auth.location, ...(auth.locations || [])].includes(
                  location,
                )) ||
              auth.roles[auth.role] >= auth.roles.GLOBAL_ADMIN
                ? listing
                : {}
            }
            src={listing.thumbnail}
          />
        </>
      ),
    },
    {
      title: '',
      key: 'address',
      className: 'address-column cross-org',
      render: (
        _,
        { line1, line2, province, city, availability, price, status, dom },
      ) => (
        <>
          <ListingInfo
            line1={line1}
            line2={line2}
            province={province}
            city={city}
            availability={availability}
            price={price}
            status={status}
            dom={dom}
          />
        </>
      ),
    },
    {
      title: '',
      key: 'autoConfirmTick',
      className: 'tick-column cross-org',
      render: (listing, { searchFromTour }) =>
        searchFromTour &&
        listing.autoConfirm && (
          <Tooltip title="Auto-Confirmed">
            <Icon
              type="antdCheckCircleFilled"
              color="success"
              width={14}
              data-cy="tag-auto-confirmed-listing"
            />{' '}
          </Tooltip>
        ),
    },
    {
      title: '',
      key: 'tag',
      className: 'tag-column cross-org',
      render: listing =>
        listing.org.isUnclaimed ? (
          <Tooltip title="This listing is not represented by BrokerBay. Please ensure to book via the correct showing service.">
            <Tag color="red" data-cy="tag-unclaimed-listing">
              Not BrokerBay
            </Tag>
          </Tooltip>
        ) : null,
    },
    {
      title: '',
      key: 'org',
      className: 'org-column',
      render: (_, { org }) =>
        options.lg && (
          <BrokerageInfo
            orgName={org.name}
            orgLogoUrl={org.logoUrl}
            dataCy="listing-org"
          />
        ),
    },
    {
      title: '',
      className: 'agents-column',
      key: 'agents',
      render: (_, { reps }) =>
        options.lg && (
          <>
            {reps.map(rep => (
              <div
                key={rep.id}
                onClick={e => e.stopPropagation()}
                data-cy={`listing-rep-${rep.id}`}
              >
                <Contact
                  avatarSize={24}
                  contact={rep}
                  underlineOnHover
                  viewNameOnToolTip
                />
              </div>
            ))}
          </>
        ),
    },
    {
      title: '',
      dataIndex: 'id',
      key: 'button',
      className: 'action-buttons-column',
      render: (
        id: string,
        {
          forceBlur,
          favorites,
          toggleFavorite,
          acceptsRegisteredOffers,
          chatAgents,
          showScheduleBtn,
          searchFromTour,
          openTourListingModal,
          thumbnail,
          line1,
          line2,
          province,
          city,
          availability,
          autoConfirm,
          price,
          status,
          dom,
          noMoreShowings,
          org,
          listingOrgHasFeature,
          preBookingInformation,
        },
        index,
      ) => (
        <ListingRowActions
          id={id}
          forceBlur={forceBlur}
          favorites={favorites}
          toggleFavorite={toggleFavorite}
          acceptsRegisteredOffers={acceptsRegisteredOffers}
          chatAgents={chatAgents}
          showScheduleBtn={showScheduleBtn}
          searchFromTour={searchFromTour}
          openTourListingModal={openTourListingModal}
          index={index}
          options={options}
          noMoreShowings={noMoreShowings}
          hasFeature={listingOrgHasFeature}
          listing={{
            id,
            thumbnail,
            line1,
            line2,
            province,
            city,
            availability,
            autoConfirm,
            preBookingInformation,
            list_price: price,
            status: status?.type,
            mls_numbers: status?.mlsNumbers,
            dom,
            org: {
              name: org.name,
              logoUrl: org.logoUrl,
              isUnclaimed: org.isUnclaimed,
            },
          }}
        />
      ),
    },
  ],
  search: async (
    debouncedQuery,
    forceBlur,
    {
      auth,
      brokerageFilter,
      favorites,
      toggleFavorite,
      searchFromTour,
      openTourListingModal,
    },
  ) => {
    const {
      data: { hits },
    } = await axios.get<ListingResponseType>('/search/listings', {
      params: {
        input: debouncedQuery,
        ...(brokerageFilter === 'allBrokerages' ? { crossOrg: true } : {}),
        ...(searchFromTour ? { searchFromTour } : {}),
      },
    });
    const data = hits.map(
      ({
        id,
        line1,
        line2,
        thumbnail,
        fullAddress,
        province,
        city,
        reps,
        list_price,
        type,
        status,
        mls_numbers,
        availability,
        location,
        org,
        dom,
        acceptsRegisteredOffers,
        noMoreShowings,
        autoConfirm,
        team,
        canEdit,
        preBookingInformation,
      }) => ({
        id,
        fullAddress,
        thumbnail,
        line1,
        line2,
        province,
        city,
        reps,
        list_price,
        type,
        status,
        mls_numbers,
        availability,
        location,
        org,
        dom,
        acceptsRegisteredOffers,
        noMoreShowings,
        autoConfirm,
        team,
        canEdit,
        preBookingInformation,
      }),
    );

    const { length } = data;

    return data
      .map(listing => mapListingResultsToTable(listing, forceBlur, length, {
          auth,
          favorites,
          toggleFavorite,
          searchFromTour,
          openTourListingModal,
        }))
      .sort((a, b) => a.value - b.value);
  },
  onConfirm: (itemSelected, { forceBlur, history }) => {
    forceBlur();
    const { id } = itemSelected;
    addListingToHistory(id);
    history.push(`/listing/${id}/view`);
  },
  createOnRow:
    ({ updateState, forceBlur, history, addListingToTour }) =>
    (item, index) => ({
      onClick: () => {
        forceBlur();
        if (addListingToTour) {
          addListingToTour(item.id);
        } else {
          addListingToHistory(item.id);
          history.push(`/listing/${item.id}/view`);
        }
      },
      onMouseMove: () => setActiveRow(index),
      onMouseLeave: () => setActiveRow(null),
    }),
});

export default getListingSearchCrossOrg;
